import { gql } from 'graphql-request';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { request } from '@utilities/graphQLClient';
import { getDateRangeByPeriod } from '@utilities/timeSeriesPeriodMap';
import type { Period } from '@utilities/timeSeriesPeriodMap';
import type { CurrencyISO } from '@/constants/currencies';

export type TimeSeriesItem = {
  date: string;
  businessRevenueSegmentsLtm: {
    [key: string]: number | null;
  } | null;
  geographicRevenueSegmentsLtm: {
    [key: string]: number | null;
  } | null;
  revenue: number | null;
  cogs: number | null;
  grossProfit: number | null;
  netIncome: number | null;
  generalAdministrative: number | null;
  salesMarketing: number | null;
  dAExpense: number | null;
  rdExpense: number | null;
  nonOperatingExpense: number | null;
  stockBasedCompensation: number | null;
  retainedEarnings: number | null;
  depreciationAmortizationTotal: number | null;
  changeNetWorkingCapital: number | null;
  leveredFreeCashFlow: number | null;
};

type NonNullableFields<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

export type TimeSeriesRawData = {
  name: string;
  timeSeries: TimeSeriesItem[] | null;
  data: {
    currencyInfo: {
      reporting_currency_iso: CurrencyISO | null;
      reporting_unit_text: string | null;
      reporting_unit: number | null;
    } | null;
  } | null;
};

export type SafeTimeSeriesRawData = {
  name: string;
  timeSeries: TimeSeriesItem[];
  data: TimeSeriesRawData['data'];
};

export type NonNilTimeSeriesRawData = NonNullableFields<TimeSeriesRawData>;

export interface QueryResponse {
  errors?: Error[];
  Company: TimeSeriesRawData;
}

export type ParamsType = {
  canonicalUrl: string;
  period: Period;
};

export type QueryKeyType = [string, string, Period];

export const COMPANY_TIME_SERIES_QUERY_KEY = 'COMPANY_TIME_SERIES';

export const getQueryKey = ({
  canonicalUrl,
  period,
}: ParamsType): QueryKeyType => [
  COMPANY_TIME_SERIES_QUERY_KEY,
  canonicalUrl,
  period,
];

const getQuery = () => gql`
  query getCompanyTimeSeries(
    $canonicalUrl: String!
    $startDate: DateTime!
    $endDate: DateTime!
  ) {
    Company(canonicalURL: $canonicalUrl) {
      name
      timeSeries(startDate: $startDate, endDate: $endDate, period: quarterly) {
        date
        businessRevenueSegmentsLtm
        geographicRevenueSegmentsLtm
        revenue
        retainedEarnings
        leveredFreeCashFlow
        depreciationAmortizationTotal
        changeNetWorkingCapital
        cogs
        grossProfit
        netIncome
        generalAdministrative
        salesMarketing
        rdExpense
        nonOperatingExpense
        stockBasedCompensation
      }
      data {
        currencyInfo
      }
    }
  }
`;

export const queryFn = async ({
  queryKey: [_, canonicalUrl, period],
}: QueryFunctionContext<QueryKeyType>) => {
  const { startDate, endDate } = getDateRangeByPeriod(period);

  const query = getQuery();

  const response = await request<QueryResponse>(query, {
    canonicalUrl,
    startDate,
    endDate,
  });

  if (response.errors) {
    const message = response.errors.map((e: Error) => e.message).join(' - ');
    throw new Error(message);
  }

  const { Company } = response;
  return Company;
};
